import type {CartStore} from '../../../stores/cartStore';
import type {UserStore} from '../../../stores/userStore';
import {PHONE_NUMBER_REGEX} from '../../../utils/validation';

// ============================================================================
// TYPES
// ============================================================================

export interface UseCheckoutStepValidationParams {
  cartStore: CartStore;
  userStore: UserStore;
}

export interface CheckoutStepValidationState {
  hasSelectedSlot: boolean;
  hasBillingAddress: boolean;
  hasDeliveryAddress: boolean;
  isDeliveryAvailable: boolean;
  isPersonAcceptingValid: boolean;
}

export interface CheckoutStepValidation {
  /** Whether user can proceed from checkout step to review */
  canProceedToReview: boolean;
  /** Individual validation states for debugging/future hint text */
  validation: CheckoutStepValidationState;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Validate nominated person details for "Someone else" option.
 * All fields required + valid AU mobile format.
 */
function isValidNominatedPerson(cartStore: CartStore): boolean {
  const {firstName, lastName, mobile} = cartStore.nominatedPersonParts;

  // All fields must be non-empty
  if (!firstName?.trim() || !lastName?.trim() || !mobile?.trim()) {
    return false;
  }

  // Mobile must match AU phone format
  return PHONE_NUMBER_REGEX.test(mobile);
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for checkout step validation.
 * Computes whether user can proceed from checkout to review step.
 *
 * Pickup Mode: selectedSlot + billingAddress
 * Delivery Mode: deliveryAddress + deliveryAvailable + selectedSlot + billingAddress + personAccepting
 */
export function useCheckoutStepValidation({
  cartStore,
  userStore,
}: UseCheckoutStepValidationParams): CheckoutStepValidation {
  // Common validation states
  const hasSelectedSlot = cartStore.selectedSlot !== null;
  const hasBillingAddress = userStore.billingAddress !== null;

  // Delivery-specific validation states
  const hasDeliveryAddress = cartStore.deliveryAddress !== null;

  // Delivery available: true if loading (don't block), false if noDeliverySlotsAvailable
  const isDeliveryAvailable = cartStore.loadingDeliverySlots
    ? true
    : !cartStore.noDeliverySlotsAvailable;

  // Person accepting validation:
  // - "Me" (willBePresent=true): always valid (user profile has required fields)
  // - "Someone else" (hasNominatedPerson=true): strict validation required
  const isPersonAcceptingValid =
    cartStore.willBePresent || (cartStore.hasNominatedPerson && isValidNominatedPerson(cartStore));

  // Compute canProceedToReview based on mode
  let canProceedToReview = false;

  if (cartStore.isPickup) {
    canProceedToReview = hasSelectedSlot && hasBillingAddress;
  } else if (cartStore.isDelivery) {
    canProceedToReview =
      hasDeliveryAddress &&
      isDeliveryAvailable &&
      hasSelectedSlot &&
      hasBillingAddress &&
      isPersonAcceptingValid;
  }

  return {
    canProceedToReview,
    validation: {
      hasSelectedSlot,
      hasBillingAddress,
      hasDeliveryAddress,
      isDeliveryAvailable,
      isPersonAcceptingValid,
    },
  };
}
